import { useCallback, useEffect, useRef, useState } from 'react'
import { useActiveEditingSectionStore, useEditorFocusStore } from '../store/conversation-store'
import { isCarouselSlideFocus } from '../utils/carousel-slide-focus'

export function useEditorHighlight(creativeId: string) {
  const setActiveSection = useActiveEditingSectionStore(s => s.setActive)
  const clearActiveSection = useActiveEditingSectionStore(s => s.clearActive)
  const consumeFocus = useEditorFocusStore(s => s.consumeFocus)
  const focusRequest = useEditorFocusStore(s => s.focusRequests[creativeId])

  const editorRef = useRef<HTMLDivElement>(null)
  const [highlightedSection, setHighlightedSection] = useState<string | null>(null)
  const highlightTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return () => {
      clearActiveSection(creativeId)
      if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current)
    }
  }, [creativeId, clearActiveSection])

  useEffect(() => {
    if (!focusRequest) return
    // Carousel slide focus is owned by MediaItemStrip — leave the request alone.
    if (isCarouselSlideFocus(focusRequest)) return
    const sectionId = consumeFocus(creativeId)
    if (!sectionId) return

    if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current)

    setHighlightedSection(sectionId)

    requestAnimationFrame(() => {
      const el = editorRef.current?.querySelector(
        `[data-section-id="${sectionId}"]`
      ) as HTMLElement | null
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    })

    highlightTimerRef.current = setTimeout(() => {
      setHighlightedSection(null)
      highlightTimerRef.current = null
    }, 1500)
  }, [focusRequest, creativeId, consumeFocus])

  const handleEditorBlur = useCallback(() => {
    requestAnimationFrame(() => {
      if (!editorRef.current?.contains(document.activeElement)) {
        clearActiveSection(creativeId)
      }
    })
  }, [creativeId, clearActiveSection])

  return { editorRef, highlightedSection, handleEditorBlur, setActiveSection }
}
